// React
import { Fragment, useContext, useEffect } from "react"

// Components
import { ActionButton, ErrorText, NumberInput, Select } from "components"

// Contexts
import { DataJournalContext } from "contexts"
import { ProductContext } from "pages/Purchase/Transaction/PurchaseOrder/components/DetailBody/contexts"

// Form
import { Controller, FormProvider, useForm, useFormContext } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"

// Types
import type { DetailFormType } from "pages/Purchase/Transaction/EnterBill/utils"

// Utils
import { convertNumber } from "utils"
import { FormType, validationSchema } from "./utils"
import { DropdownJobs } from "./DropdownJobs"
import { useWarehouseByLocation } from "./utils/hooks/useWarehouseByLocation"

export function FormSection(params: {
  onSuccess: (value: DetailFormType) => void
  value: DetailFormType
  toggle: () => void
  location_id: string
  isInventory: boolean
}): JSX.Element {
  // Hooks
  const { data } = useContext(ProductContext)
  const { tax_code } = useContext(DataJournalContext)
  const { data: warehouseOptions, isLoading: warehouseLoading } = useWarehouseByLocation(params.location_id)

  // Form
  const methods = useForm<FormType>({
    defaultValues: params.value,
    resolver: yupResolver(validationSchema)
  })
  const onSubmit = (value: DetailFormType) => {
    params.onSuccess(value)
  }

  // Vars
  const selectedData = data.find(item => item.value === methods.getValues("product_id"))
  const tax_name: string | undefined = tax_code.data.find(i => i.value === methods.getValues("tax_id"))?.label
  const location_warehouse_id = methods.watch("location_warehouse_id")
  const noInventory = !params.isInventory
  const noWarehouse = !warehouseLoading && warehouseOptions.length === 0
  const disableWarehouse = noInventory || noWarehouse || warehouseLoading

  useEffect(() => {
    if (noInventory && location_warehouse_id !== null) {
      methods.setValue("location_warehouse_id", null)
    }
  }, [noInventory, location_warehouse_id, methods])

  return (
    <FormProvider {...methods}>
      <tr>
        <td>{methods.getValues("qty_order")}</td>

        <td>
          <Controller
            name="quantity_bill"
            control={methods.control}
            render={({ field, fieldState }) => (
              <NumberInput
                // decimalScale={0}
                error={fieldState.error?.message}
                ref={field.ref}
                value={field.value}
                onValueChange={e => {
                  field.onChange(e.floatValue)

                  // Vars
                  const value: number = typeof e.floatValue !== "number" ? 1 : e.floatValue
                  const discount: number = methods.getValues("discount")
                  const price_bill: number = methods.getValues("price_bill")
                  const qty_multiply: number = methods.getValues("qty_multiply")
                  const total: number = price_bill * value

                  methods.setValue("quantity_unit", qty_multiply * value)
                  methods.setValue("total", total - (total * discount / 100))
                }}
              />
            )}
          />
        </td>

        <Product />
        <td>{selectedData?.mr_unit_name}</td>
        <td className="text-right">{convertNumber(methods.getValues("price_bill")).intoCurrency}</td>
        <td>{convertNumber(methods.getValues("discount")).intoNormalAmount}%</td>
        <td>
          <Controller
            name="total"
            control={methods.control}
            render={({ field }) => <div className="text-right">{convertNumber(field.value).intoCurrency}</div>}
          />
        </td>
        <td>
          <DropdownJobs />
        </td>
        <td>{tax_name}</td>

        <td>
          <Controller
            name="location_warehouse_id"
            control={methods.control}
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-0.5">
                <Select
                  placeholder={noInventory ? "Non inventory item" : noWarehouse ? "No warehouse" : "Select warehouse"}
                  isDisabled={disableWarehouse}
                  isLoading={warehouseLoading}
                  options={warehouseOptions}
                  value={warehouseOptions.find(o => o.value === field.value) ?? null}
                  onChange={(e: any) => field.onChange(e ? e.value : null)}
                />
                {fieldState.error?.message ? (
                  <ErrorText text={fieldState.error.message} />
                ) : null}
              </div>
            )}
          />
        </td>

        <td>
          <section className="flex justify-center">
            <ActionButton.Check onClick={methods.handleSubmit(onSubmit)} />

            <ActionButton.Close
              className="text-error"
              onClick={params.toggle}
            />
          </section>
        </td>
      </tr>
    </FormProvider>
  )
}

function Product() {
  // Form
  const { getValues } = useFormContext<FormType>()

  // Hooks
  const { data } = useContext(ProductContext)

  // Vars
  const selectedData = data.find(item => item.value === getValues("product_id"))

  return (
    <Fragment>
      <td>{selectedData?.product_barcode}</td>
      <td>{selectedData?.product_name}</td>
    </Fragment>
  )
}